//@ts-nocheck
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { logEvent, setUserId } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import CoBrandedLogo from '../../../componentsv2/CoBrandedLogo';
import CreatePasswordSkeleton from './skeleton';
import { usePartnerByReferralId } from '../../../hooks/usePartners';
import Typography from '../../../componentsv2/Typography';
import Form from '../../../componentsv2/Form';
import { TextField } from '../../../componentsv2/Field';
import Button from '../../../componentsv2/Button';
import TermsAndConditionsLabel from '../../../componentsv2/TermsAndConditionsLabel';
import { currentUserDetail } from '../../../services/companyUsers';
import { loadUser as loadUserThunk, setUserPassword, useAppDispatch } from '../../../store';
import { useLazyTranslation } from '../../../hooks/i18n';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { signupEvents } from '../../../constants/events';
import { DASHBOARD, EMAIL_VERIFICATION, ACTIVATION } from '../../../constants/auth';
import { identifyUser } from '../../../utils/tracking';
import { hasDigit, hasLowerCase, hasUpperCase, mapLandingPages } from '../../../utils/helpers';
import { removeAppsCurrentStep } from '../../../utils/common';
import { NewPasswordSchema } from '../../../schemas';

const CreatePassword = () => {
  const { t } = useLazyTranslation(['common']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { search, pathname } = useLocation();
  const [error, setError] = useState<Error>();
  const params = new URLSearchParams(search);
  const token = params.get('token');
  const email = params.get('email');
  const landingPage = params.get('landing_page');
  const reset = params.get('reset');
  const referralId = params.get('referral_id');
  const product = mapLandingPages[landingPage as keyof typeof mapLandingPages] || DASHBOARD;
  const { language } = useUserLocation();
  const { partner, isLoadingPartner } = usePartnerByReferralId(referralId);

  useEffect(() => {
    // Ensure AuthGuard redirects new signups to activation instead of dashboard.
    if (reset) return;

    const next = params.get('next');
    if (!next) {
      params.set('next', ACTIVATION);
      navigate(`${pathname}?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, search, pathname, navigate]);

  const submit = ({ password }: { password: string }) => {
    if (!token) return;
    setIsLoading(true);
    dispatch(setUserPassword({ token, password }))
      .unwrap()
      .then(() => {
        setIsLoading(false);
        !reset && removeAppsCurrentStep();
        dispatch(loadUserThunk())
          .unwrap()
          .then(() => {
            currentUserDetail().then(({ country, selectedCompany, name, email }) => {
              identifyUser({
                country,
                companyName: selectedCompany.name,
                name,
                email,
                language,
              }).promise.then(() => {
                const event = !!reset ? signupEvents.PASSWORD_RESET : signupEvents.PASSWORD_SET;
                logEvent(event, { Email: email });
                // Redirect to activation for new signups (not password resets)
                if (!reset) {
                  navigate(ACTIVATION);
                } else {
                  navigate(product);
                }
              });
            });
          });
      })
      .catch((error) => {
        setIsLoading(false);
        setError(error);
        navigate(EMAIL_VERIFICATION);
      });
  };

  useEffect(() => {
    // queryparams are encoded and the plus sign becomes a whitespace (this cleaning is only useful for dev purposes)
    email && setUserId(email.replace(' ', '+'));
    logEvent(signupEvents.VIEW_SET_PASSWORD, { Email: email });
  }, []);

  if (isLoadingPartner) return <CreatePasswordSkeleton />;

  return (
    <>
      <TopBar>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CoBrandedLogo partnerLogoUrl={partner?.logoUrl} partnerName={partner?.name}>
            <IconButton href="/">
              <MarcoLogo />
            </IconButton>
          </CoBrandedLogo>
        </Box>
      </TopBar>
      <Grid xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1em',
            paddingTop: { xs: '4em', md: '0' },
            width: { xs: '80%', md: '30%' },
            textAlign: 'center',
          }}
        >
          <Typography variant="h4">{t('common:createPassword.title')}</Typography>
          <Typography>{t('common:createPassword.subtitle')}</Typography>
          <Formik
            onSubmit={submit}
            initialValues={{ password: '', confirmPassword: '' }}
            validationSchema={NewPasswordSchema(t)}
          >
            {({ values, isValid, dirty }) => {
              return (
                <Form>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      type="password"
                      fullWidth
                      name="password"
                      label={t('common:createPassword.passwordLabel')}
                      placeholder={t('common:createPassword.passwordPlaceholder')}
                    />
                    <TextField
                      type="password"
                      fullWidth
                      name="confirmPassword"
                      label={t('common:createPassword.confirmPasswordLabel')}
                      placeholder={t('common:createPassword.confirmPasswordPlaceholder')}
                    />
                    <Grid container sx={{ marginTop: '1em', textAlign: 'left' }} rowSpacing={1}>
                      <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        {values?.password && values.password.length >= 8 && values.password.length <= 20 ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                        <Typography>{t('common:createPassword.passwordLengthCriteria')}</Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        {values?.password &&
                        hasUpperCase(values.password) &&
                        hasLowerCase(values.password) ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                        <Typography>
                          {t('common:createPassword.passwordUpperAndLowercaseCriteria')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        {values?.password && hasDigit(values.password) ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                        <Typography>{t('common:createPassword.passwordDigitCriteria')}</Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ marginTop: '2em' }}>
                        <TermsAndConditionsLabel
                          firstPart={`${t('common:signup.termsAndConditionsFirstPart')} `}
                          sx={{ textAlign: 'center' }}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ marginTop: '4em' }}>
                      <Button
                        fullWidth
                        color="secondary"
                        isSubmit
                        disabled={!dirty || !isValid}
                        isLoading={isLoading}
                      >
                        {t('common:createPassword.createPassword')}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Grid>
    </>
  );
};

export default CreatePassword;
